import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast, toastErrorMessage } from "../lib/toast";
import Sheet from "./Sheet";
import { Camera, Upload, Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";

// Read a File as base64 (no data URL prefix) - keeps the payload small & predictable.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result || "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

function ConfidencePill({ level }) {
  if (level !== "low" && level !== "medium") return null;
  const cls = level === "low"
    ? "bg-yellow-100 text-yellow-900 border border-yellow-300"
    : "bg-orange-50 text-orange-800 border border-orange-200";
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${cls}`}
          title="AI guess - please confirm">
      <Sparkles size={9} className="inline mr-1 -mt-0.5"/>
      AI guess
    </span>
  );
}

function Field({ label, value, confidence, onChange, type = "text", testid }) {
  const isShaky = confidence === "low" || confidence === "medium";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="cesco-label !mb-0">{label}</label>
        <ConfidencePill level={confidence}/>
      </div>
      <input type={type} value={value || ""} onChange={e=>onChange(e.target.value)}
             data-testid={testid}
             className={`cesco-input ${isShaky ? "bg-yellow-50 border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500" : ""}`}/>
    </div>
  );
}

export default function BusinessCardScanner({ onClose, onCreated }) {
  const { meta } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const [stage, setStage] = useState("capture");  // "capture" | "scanning" | "review" | "saving"
  const [extracted, setExtracted] = useState(null);
  const [confidence, setConfidence] = useState({});
  const [match, setMatch] = useState(null);
  const [companyDecision, setCompanyDecision] = useState(null);  // "use_existing" | "create_new"
  const [company, setCompany] = useState({});
  const [contact, setContact] = useState({});

  async function handleFile(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB). Try a tighter crop or lower resolution.");
      return;
    }
    setStage("scanning");
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/business-cards/scan", {
        image_base64: b64,
        mime_type: file.type || "image/jpeg",
      });
      setExtracted(data.extracted);
      setConfidence(data.confidence || {});
      setMatch(data.match || { kind: "new" });
      setCompany({ ...(data.extracted?.company || {}) });
      setContact({ ...(data.extracted?.contact || {}) });
      // Default decision: if exact match, prefill to "use existing"
      setCompanyDecision(data.match?.kind === "exact" ? "use_existing" : (data.match?.kind === "possible" ? null : "create_new"));
      setStage("review");
    } catch(e) {
      toast.error(toastErrorMessage(e));
      setStage("capture");
    }
  }

  async function save() {
    if (!contact.first_name?.trim() || !contact.last_name?.trim()) {
      toast.error("Contact must have a first and last name");
      return;
    }
    if (companyDecision !== "use_existing" && !company.company_name?.trim()) {
      toast.error("Company name is required");
      return;
    }
    setStage("saving");
    try {
      let companyId = null;
      if (companyDecision === "use_existing") {
        companyId = match.company_id;
      } else {
        // Create new company. Sensible defaults for required fields.
        const payload = {
          company_name: company.company_name.trim(),
          state: (company.state || "QLD").toUpperCase(),
          customer_tier: 6,  // "Have not made contact" - fits a fresh card scan
          phone: company.phone || "",
          website: company.website || "",
          street: company.street || "",
          suburb: company.suburb || "",
          postcode: company.postcode || "",
          country: company.country || "Australia",
          generic_email: company.generic_email || "",
          product_segments: [],
        };
        const { data } = await api.post("/companies", payload);
        companyId = data.id;
      }
      // Always create the contact.
      const cpayload = {
        contact_title: contact.contact_title || null,
        first_name: contact.first_name.trim(),
        last_name: contact.last_name.trim(),
        position: contact.position || "",
        direct_email: contact.direct_email || "",
        phone_office: contact.phone_office || "",
        phone_mobile: contact.phone_mobile || "",
      };
      await api.post(`/companies/${companyId}/contacts`, cpayload);
      toast.success(companyDecision === "use_existing"
        ? `Contact added to ${match.company_name}`
        : `Created ${company.company_name} + contact`);
      onCreated?.(companyId);
      onClose();
      navigate(`/companies/${companyId}?tab=Contacts`);
    } catch(e) {
      toast.error(toastErrorMessage(e));
      setStage("review");
    }
  }

  // ---------- STAGE: CAPTURE ----------
  if (stage === "capture") {
    return (
      <Sheet open onClose={onClose} title="Scan business card"
             footer={<button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>}>
        <div className="space-y-4 text-center">
          <p className="text-sm text-gray-600">Snap a photo of the card. We'll extract the details for you to review.</p>
          <div className="grid grid-cols-2 gap-3">
            <button className="cesco-card p-6 flex flex-col items-center gap-2 hover:shadow-md transition"
                    onClick={()=>cameraRef.current?.click()}
                    data-testid="scan-camera-btn">
              <Camera size={32} className="text-blue-600"/>
              <span className="font-semibold">Use camera</span>
              <span className="text-xs text-gray-500">Best on mobile</span>
            </button>
            <button className="cesco-card p-6 flex flex-col items-center gap-2 hover:shadow-md transition"
                    onClick={()=>fileRef.current?.click()}
                    data-testid="scan-upload-btn">
              <Upload size={32} className="text-gray-700"/>
              <span className="font-semibold">Upload photo</span>
              <span className="text-xs text-gray-500">JPG / PNG · max 8 MB</span>
            </button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                 onChange={e=>handleFile(e.target.files?.[0])} className="hidden"/>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                 onChange={e=>handleFile(e.target.files?.[0])} className="hidden"/>
        </div>
      </Sheet>
    );
  }

  // ---------- STAGE: SCANNING ----------
  if (stage === "scanning") {
    return (
      <Sheet open onClose={()=>{}} title="Reading the card…">
        <div className="flex flex-col items-center gap-3 py-8" data-testid="scan-progress">
          <Loader2 size={36} className="animate-spin text-blue-600"/>
          <p className="text-sm text-gray-700">Extracting fields with Gemini…</p>
          <p className="text-xs text-gray-500">Usually takes 5–10 seconds.</p>
        </div>
      </Sheet>
    );
  }

  // ---------- STAGE: REVIEW ----------
  const matchBanner = (() => {
    if (!match) return null;
    if (match.kind === "exact") {
      return (
        <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm" data-testid="match-exact">
          <div className="flex items-center gap-2 text-green-900 font-semibold">
            <CheckCircle2 size={16}/> Match found: {match.company_name}
          </div>
          <div className="text-xs text-green-800 mt-1">{match.reason}</div>
          <div className="mt-2 flex gap-3 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={companyDecision === "use_existing"}
                     onChange={()=>setCompanyDecision("use_existing")} data-testid="decision-use-existing"/>
              Add contact to <b>{match.company_name}</b>
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={companyDecision === "create_new"}
                     onChange={()=>setCompanyDecision("create_new")} data-testid="decision-create-new"/>
              Create new instead
            </label>
          </div>
        </div>
      );
    }
    if (match.kind === "possible") {
      return (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="match-possible">
          <div className="flex items-center gap-2 text-amber-900 font-semibold">
            <AlertCircle size={16}/> This looks similar to: {match.company_name}
          </div>
          <div className="text-xs text-amber-800 mt-1">{match.reason}</div>
          <div className="mt-2 flex gap-3 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={companyDecision === "use_existing"}
                     onChange={()=>setCompanyDecision("use_existing")} data-testid="decision-use-existing"/>
              Add contact to <b>{match.company_name}</b>
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={companyDecision === "create_new"}
                     onChange={()=>setCompanyDecision("create_new")} data-testid="decision-create-new"/>
              Create new company
            </label>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm" data-testid="match-new">
        <div className="flex items-center gap-2 text-gray-800 font-semibold">
          <Sparkles size={16}/> New company will be created
        </div>
        <div className="text-xs text-gray-600 mt-1">No existing company matches this card.</div>
      </div>
    );
  })();

  const showCompanyForm = companyDecision !== "use_existing";
  const c = confidence;

  return (
    <Sheet open onClose={onClose}
           title="Review extracted details"
           footer={
             <div className="flex gap-2 justify-end flex-wrap">
               <button className="cesco-btn-ghost" onClick={onClose} data-testid="scan-cancel">Cancel</button>
               <button className="cesco-btn-primary" onClick={save}
                       disabled={stage === "saving" || !companyDecision}
                       data-testid="scan-save">
                 {stage === "saving" ? "Saving…" : (companyDecision === "use_existing" ? "Add contact" : "Create company + contact")}
               </button>
             </div>
           }>
      <div className="space-y-5">
        {matchBanner}

        {showCompanyForm && (
          <section className="space-y-3">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">Company</h3>
            <Field label="Company name *" value={company.company_name} confidence={c["company.company_name"]}
                   onChange={v=>setCompany({...company, company_name: v})} testid="scan-company-name"/>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" value={company.phone} confidence={c["company.phone"]}
                     onChange={v=>setCompany({...company, phone: v})} testid="scan-company-phone"/>
              <Field label="Website" value={company.website} confidence={c["company.website"]}
                     onChange={v=>setCompany({...company, website: v})} testid="scan-company-website"/>
              <Field label="Street" value={company.street} confidence={c["company.street"]}
                     onChange={v=>setCompany({...company, street: v})}/>
              <Field label="Suburb" value={company.suburb} confidence={c["company.suburb"]}
                     onChange={v=>setCompany({...company, suburb: v})}/>
              <Field label="Postcode" value={company.postcode} confidence={c["company.postcode"]}
                     onChange={v=>setCompany({...company, postcode: v})}/>
              <Field label="State" value={company.state} confidence={c["company.state"]}
                     onChange={v=>setCompany({...company, state: v.toUpperCase()})}/>
            </div>
            <Field label="Generic email" value={company.generic_email} confidence={c["company.generic_email"]}
                   onChange={v=>setCompany({...company, generic_email: v})}/>
          </section>
        )}

        <section className="space-y-3">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">Contact</h3>
          <div className="grid grid-cols-[80px_1fr_1fr] gap-3">
            <Field label="Title" value={contact.contact_title} confidence={c["contact.contact_title"]}
                   onChange={v=>setContact({...contact, contact_title: v})}/>
            <Field label="First name *" value={contact.first_name} confidence={c["contact.first_name"]}
                   onChange={v=>setContact({...contact, first_name: v})} testid="scan-first-name"/>
            <Field label="Last name *" value={contact.last_name} confidence={c["contact.last_name"]}
                   onChange={v=>setContact({...contact, last_name: v})} testid="scan-last-name"/>
          </div>
          <Field label="Position" value={contact.position} confidence={c["contact.position"]}
                 onChange={v=>setContact({...contact, position: v})}/>
          <Field label="Direct email" type="email" value={contact.direct_email} confidence={c["contact.direct_email"]}
                 onChange={v=>setContact({...contact, direct_email: v})} testid="scan-direct-email"/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Office phone" value={contact.phone_office} confidence={c["contact.phone_office"]}
                   onChange={v=>setContact({...contact, phone_office: v})}/>
            <Field label="Mobile" value={contact.phone_mobile} confidence={c["contact.phone_mobile"]}
                   onChange={v=>setContact({...contact, phone_mobile: v})} testid="scan-phone-mobile"/>
          </div>
        </section>

        <p className="text-xs text-gray-500">
          <Sparkles size={11} className="inline -mt-0.5 mr-1"/>
          Yellow fields are AI guesses - please double-check them before saving.
        </p>
      </div>
    </Sheet>
  );
}
